// src/components/ReferralChainModal.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Modal, Button, Spinner, Alert, Card, Row, Col, Badge } from 'react-bootstrap';
import { FaCodeBranch, FaCheckCircle, FaHourglassHalf, FaUser } from 'react-icons/fa';

const ReferralChainModal = ({ show, handleClose, investmentId }) => {
    const [chainData, setChainData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (show && investmentId) {
            setLoading(true);
            setError(null);
            setChainData(null);
            
            const fetchChainDetails = async () => {
                try {
                    const token = localStorage.getItem('token');
                    const config = {
                        headers: {
                            'Content-Type': 'application/json',
                            'x-auth-token': token
                        }
                    };
                    
                    const res = await axios.get(`/api/investment/chain-details/${investmentId}`, config);
                    setChainData(res.data.level1);
                    
                } catch (err) {
                    console.error('Erreur de chargement de la chaîne:', err);
                    setError(err.response?.data?.message || 'Erreur lors du chargement des détails de la chaîne.');
                } finally {
                    setLoading(false);
                }
            };

            fetchChainDetails();
        }
    }, [show, investmentId]);

    // Composant de Nœud
    const ChainNode = ({ user, isPhantom, level }) => {
        const title = isPhantom ? `Slot Vide` : (user || 'Nœud Inconnu');
        const bgColor = isPhantom ? 'bg-light text-muted' : 'bg-primary text-white';
        const icon = isPhantom ? <FaHourglassHalf /> : <FaUser />;

        return (
            <Card className={`text-center h-100 ${bgColor} shadow-sm border-0`}>
                <Card.Body className="p-2">
                    <div className="fs-5">{icon}</div>
                    <small className="d-block mt-1 fw-bold">{title}</small>
                    <Badge bg="dark" className="mt-1">{`Niveau ${level}`}</Badge>
                </Card.Body>
            </Card>
        );
    };

    return (
        <Modal show={show} onHide={handleClose} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title><FaCodeBranch className="me-2" />Détails de la Chaîne de Parrainage</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading && <div className="text-center p-4"><Spinner animation="border" /><p>Chargement de l'arbre...</p></div>}
                {error && <Alert variant="danger">{error}</Alert>}
                
                {chainData && (
                    <>
                        <Alert variant={chainData.isCompleted ? "success" : "info"} className="mb-3">
                            Statut: 
                            <span className="fw-bold ms-2">
                                {chainData.isCompleted ? 
                                    `Complétée! (Gain double déclenché) - Avancement: ${chainData.progress}/${chainData.totalRequired}` : 
                                    `Active - Progrès: ${chainData.progress}/${chainData.totalRequired} Filleuls Recrutés.`
                                }
                            </span>
                        </Alert>

                        {/* Niveau 1 : L'Investissement de l'Utilisateur Actuel */}
                        <div className="text-center mb-4">
                            <h5 className="mb-2 text-primary">Niveau 1 (Vous)</h5>
                            <Card className="d-inline-block p-2 bg-light shadow-sm">
                                <span className="fw-bold">{chainData.username}</span> - {chainData.amount.toLocaleString()} FCFA
                            </Card>
                            <div className="text-muted mt-2">Lien: **{chainData.referralLink}**</div>
                        </div>

                        {/* Niveau 2 : Filleuls Directs (2 max) */}
                        <h6 className="mt-3 text-center">Niveau 2 (Filleuls directs)</h6>
                        <Row className="justify-content-center g-2 mb-3">
                            {chainData.level2.map((level2Inv, index) => (
                                <Col xs={6} md={4} key={`L2-${index}`}>
                                    <ChainNode 
                                        user={level2Inv.username} 
                                        isPhantom={level2Inv.isPhantom}
                                        level={2}
                                    />
                                </Col>
                            ))}
                        </Row>

                        {/* Niveau 3 : Petits-Filleuls (4 max) */}
                        <h6 className="mt-4 text-center">Niveau 3 (Petits-filleuls)</h6>
                        <Row className="justify-content-center g-2">
                            {chainData.level2.flatMap((level2Inv, indexL2) => 
                                level2Inv.level3.map((level3Inv, indexL3) => (
                                    <Col xs={6} sm={3} key={`L3-${indexL2}-${indexL3}`}>
                                        <ChainNode 
                                            user={level3Inv.username} 
                                            isPhantom={level3Inv.isPhantom}
                                            level={3}
                                        />
                                    </Col>
                                ))
                            )}
                        </Row>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Fermer
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ReferralChainModal;